import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '../../../context/StudentAuthContext';
import { toast } from 'react-hot-toast';

const StudentQuizTaking = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { studentApi } = useStudentAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [attemptId, setAttemptId] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizDetails, setQuizDetails] = useState(null);

  useEffect(() => {
    initializeQuiz();
  }, [quizId]);

  useEffect(() => {
    if (timeRemaining === 0 && attemptId) {
      handleAutoSubmit();
    }
  }, [timeRemaining]);

  const initializeQuiz = async () => {
    try {
      setLoading(true);
      
      // Start or resume quiz attempt
      const attempt = await studentApi.post(`/student/quizzes/${quizId}/start`);
      setAttemptId(attempt.data.id);
      
      // Get quiz details
      const quizData = await studentApi.get(`/student/quizzes/${quizId}`);
      setQuiz(quizData.data);
      setQuizDetails(quizData.data);
      
      // Get questions for this attempt - the response has a 'questions' array
      const questionsData = await studentApi.get(`/student/quizzes/attempts/${attempt.data.id}/questions`);
      // The questions are in questionsData.data.questions array
      const questionsList = questionsData.data.questions || questionsData.data;
      setQuestions(questionsList);
      
      // Get any previously saved answers
      const answersData = await studentApi.get(`/student/quizzes/attempts/${attempt.data.id}/answers`);
      const ansMap = {};
      const feedbackMap = {};
      const answersList = answersData.data || [];
      answersList.forEach((ans) => {
        ansMap[ans.question_id] = ans.answer_text;
        if (ans.is_correct !== undefined) {
          feedbackMap[ans.question_id] = ans.is_correct;
        }
      });
      setAnswers(ansMap);
      setFeedback(feedbackMap);
      
      // Get time remaining
      const timeData = await studentApi.get(`/student/quizzes/attempts/${attempt.data.id}/time-remaining`);
      setTimeRemaining(timeData.data.remaining_seconds);
      
      // Start timer
      const timer = setInterval(() => {
        setTimeRemaining(prev => Math.max(0, (prev || 0) - 1));
      }, 1000);
      
      return () => clearInterval(timer);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load quiz');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSubmit = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      await studentApi.post(`/student/quizzes/attempts/${attemptId}/submit`);
      toast.success('Quiz auto-submitted due to time out');
      navigate(`/student/quiz/${quizId}/attempts`);
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = async (questionId, selectedOption) => {
    if (feedback[questionId] !== undefined && quiz?.show_answers_immediately) {
      return;
    }

    try {
      const saved = await studentApi.post(`/student/quizzes/attempts/${attemptId}/answer`, {
        question_id: questionId,
        answer_text: selectedOption,
      });

      setAnswers(prev => ({ ...prev, [questionId]: selectedOption }));

     

      if (quiz?.show_answers_immediately) {
        setShowExplanation(true);
         if (saved.data.is_correct !== undefined) {
        setFeedback(prev => ({ ...prev, [questionId]: saved.data.is_correct }));

        if (saved.data.is_correct) {
          toast.success('Correct!', { autoClose: 1000 });
        } else {
          toast.error('Incorrect', { autoClose: 1000 });
        }
      }
      
        setTimeout(() => setShowExplanation(false), 3000);
      }
    } catch (error) {
      toast.error('Failed to save answer');
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!window.confirm('Are you sure you want to submit this quiz?')) return;

    setSubmitting(true);
    try {
      await studentApi.post(`/student/quizzes/attempts/${attemptId}/submit`);
      toast.success('Quiz submitted successfully!');
      navigate(`/student/quiz/${quizId}/attempts`);
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (timeRemaining === null) return 'text-gray-600';
    if (timeRemaining > 300) return 'text-green-600';
    if (timeRemaining > 60) return 'text-yellow-600';
    return 'text-red-600 animate-pulse';
  };

  const getOptionLabel = (index) => String.fromCharCode(65 + index);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No questions available</p>
        <Link to="/student/quizzes" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Quizzes
        </Link>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const isAnswered = answers[currentQ.id] !== undefined;
  const isCorrect = feedback[currentQ.id];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="space-y-6">
      {/* Header with Timer */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4 sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{quiz?.title}</h1>
            <p className="text-sm text-gray-500">Question {currentIndex + 1} of {questions.length}</p>
          </div>
          <div className="flex items-center gap-4">
            {timeRemaining !== null && (
              <div className="text-right">
                <div className={`text-2xl font-mono font-bold ${getTimeColor()}`}>
                  {formatTime(timeRemaining)}
                </div>
                <div className="text-xs text-gray-500">Time remaining</div>
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Quiz'}
            </button>
          </div>
        </div>
        <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
          <div className="bg-green-500 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-4">
          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
            {currentQ.question_type}
          </span>
          {isAnswered && quiz?.show_answers_immediately && (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
              isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {isCorrect ? 'Correct' : 'Incorrect'}
            </span>
          )}
        </div>

        <div
          className="text-gray-800 mb-6 text-lg prose max-w-none"
          dangerouslySetInnerHTML={{ __html: currentQ.question_text }}
        />

        {currentQ.question_type === 'mcq' && currentQ.options ? (
          <div className="space-y-3">
            {currentQ.options.map((option, idx) => {
              // Handle option object with label and text properties
              const optionLabel = option.label || getOptionLabel(idx);
              const optionText = option.text || option;
              const isSelected = answers[currentQ.id] === optionLabel;
              const isCorrectAnswer = optionLabel === currentQ.correct_answer;
              const isCorrectStatus = option.status === 'correct';
              
              let borderClass = 'border-gray-200 hover:border-blue-300 hover:bg-blue-50';
              if (isAnswered && quiz?.show_answers_immediately) {
                if (isCorrectAnswer || isCorrectStatus) borderClass = 'border-green-500 bg-green-50';
                else if (isSelected && !isCorrectAnswer && !isCorrectStatus) borderClass = 'border-red-500 bg-red-50';
                else borderClass = 'border-gray-200 opacity-60';
              } else if (isSelected) {
                borderClass = 'border-blue-500 bg-blue-50';
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswer(currentQ.id, optionLabel)}
                  disabled={isAnswered && quiz?.show_answers_immediately}
                  className={`w-full text-left p-4 border-2 rounded-xl transition-all ${borderClass}`}
                >
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 mr-3 min-w-6">{optionLabel}.</span>
                    <span dangerouslySetInnerHTML={{ __html: optionText }} className="text-gray-700" />
                    {isAnswered && quiz?.show_answers_immediately && (isCorrectAnswer || isCorrectStatus) && (
                      <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded">Correct</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <textarea
            value={answers[currentQ.id] || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setAnswers({ ...answers, [currentQ.id]: newValue });
              studentApi.post(`/student/quizzes/attempts/${attemptId}/answer`, {
                question_id: currentQ.id,
                answer_text: newValue,
              }).catch(console.error);
            }}
            disabled={isAnswered && quiz?.show_answers_immediately}
            rows={6}
            className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Type your answer here..."
          />
        )}

        {showExplanation && currentQ.explanation && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="font-medium text-blue-800 mb-1">Explanation:</p>
            <p className="text-blue-700 text-sm">{currentQ.explanation}</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
          disabled={currentIndex === 0}
          className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        {currentIndex === questions.length - 1 ? (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Finish Quiz'}
          </button>
        ) : (
          <button
            onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Next
          </button>
        )}
      </div>

      {/* Question Navigator - Quick Jump */}
      {questions.length > 10 && (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Quick Navigation</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                  currentIndex === idx
                    ? 'bg-blue-600 text-white shadow-md'
                    : answers[questions[idx]?.id]
                    ? 'bg-green-100 text-green-800 border border-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentQuizTaking;